import React, { lazy, Suspense, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Loading, useFeatures, ListGroupItem, useSession } from '@cordata/buffet';
import { ListGroup } from 'reactstrap';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash';

import PatientsBannerModal from '~/components/Patient/Identity/Banner/Modal';
import { PageLayout, Navbar } from '~/components';

import {
  authorizedPyInstance,
} from '~/utils';

import PatientSearchIdentityCard from '~/components/Patient/Identity/Search/Card';

const Coordinator = lazy(() => import('./coordinator'));
const Community = lazy(() => import('./community'));
const HWC = lazy(() => import('./hwc'));
const Sponsor = lazy(() => import('./sponsor'));
const Identity = lazy(() => import('./identity'));
const Cit = lazy(() => import('./cit'));

const SideNav = ({ items }) => {
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [id, setId] = useState();
  const {
    hasAppIdentity,
  } = useFeatures();
  const [t] = useTranslation();

  const toggleBannerModal = () => {
    setId(undefined);
    setIsBannerModalOpen(!isBannerModalOpen);
  };

  useEffect(() => {
    setIsBannerModalOpen(!!id);
  }, [id]);

  return (
    <ListGroup className="side-nav rounded-0" flush>
      <ListGroupItem
        disabled
        className="side-nav-item rounded-0 border-dark border-bottom-0 has-children is-open"
      >
        Recent {capitalize(t('patients'))}
      </ListGroupItem>

      <ListGroup>
        {items &&
          items?.map(i => (
            <ListGroupItem
              tag={Link}
              to={!hasAppIdentity && `/patients/${i.attributes.patientUid}`}
              className="side-nav-item rounded-0 border-dark is-child"
              onClick={() => setId(i.attributes.patientUid)}
            >
              {i.attributes.firstName} {i.attributes.lastName}
            </ListGroupItem>
          ))}
      </ListGroup>
      <PatientsBannerModal
        isOpen={isBannerModalOpen}
        toggleIsOpen={toggleBannerModal}
        patient={id}
      />
    </ListGroup>
  );
};

SideNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()),
};

SideNav.defaultProps = {
  items: undefined,
};

export { SideNav };

const LeftHand = () => {
  const [sideNavItems, setSideNavItems] = useState();
  const { current } = useSession();
  const history = useHistory();
  const {
    hasAppIdentity,
  } = useFeatures();

  const handleAfterSubmit = ({ values }) =>
    history.push('/patients', { searched: true, values });

  useEffect(() => {
    if (current) {
      const endpoint = hasAppIdentity ? '/api/data/public/list_view_cs_recent_patients' : '/api/data/public/nav_list_view_recent_patients';
      authorizedPyInstance
        .get(endpoint, {
          params: {
            user_uid: current.attributes.userUid,
            page: 1,
            'page-size': 4,
            sort: '-updated-date',
          },
        })
        .then(r => r.data.data)
        .then(setSideNavItems);
    }
  }, [current]);

  return (
    <>
      {hasAppIdentity && <PatientSearchIdentityCard afterSubmit={handleAfterSubmit} />}

      {sideNavItems?.length && <SideNav items={sideNavItems} />}
    </>
  );
};

const Home = () => {
  const {
    hasAppIdentity, hasAppCommunity, hasHwc, hasAppNavigator, twoOh, hasAppSponsor, hasAppCit,
  } = useFeatures();

  let Component = null;

  if (hasAppIdentity) {
    Component = Identity;
  } else if (hasAppSponsor) {
    Component = Sponsor;
  } else if (hasHwc) {
    Component = HWC;
  } else if (hasAppNavigator && twoOh) {
    Component = Coordinator;
  } else if (hasAppCit) {
    Component = Cit;
  } else if (hasAppCommunity) {
    Component = Community;
  }

  return (
    <PageLayout
      title="Home"
      Header={Navbar}
      Left={hasAppIdentity || hasAppCit ? <LeftHand /> : null}
      centerMain={false}
    >
      {Component
        ? (
          <Suspense fallback={<Loading />}>
            <Component />
          </Suspense>
        ) : <Loading message="Checking roles and permissions" />
      }
    </PageLayout>
  );
};

export default Home;
